import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RequestType } from '../../models/request-type.model';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CodeModel } from '../../models/code.model';
import { CommonModule } from '@angular/common';
import { PersonSearchComponent } from '../../../shared/person-search/person-search.component';
import { RequestAndLineTypeService } from '../../services/request-and-line-types.service';
import { SharedService } from '../../../services/common/shared.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LoggedInUserService } from '../../../services/common/logged-in-user.service';
import { ILoggedInUser } from '../../../interfaces/common/loggedInUser';
import { approverTypes, doaTypes } from '../../administration.const';

@Component({
  selector: 'app-manage-request-type',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, PersonSearchComponent, MatProgressSpinnerModule, MatDialogModule],
  templateUrl: './manage-request-type.component.html',
  styleUrl: './manage-request-type.component.scss'
})
export class ManageRequestTypeComponent implements OnInit {

  user: ILoggedInUser;
  isLoading: boolean = true;
  requestTypeData: RequestType;
  isRequestTypeUsed: boolean = true;
  form: FormGroup;
  errorResponse: string;
  successResponse: string;
  employeeTypeCodes: CodeModel[];
  doaTypes = doaTypes;
  approverTypes = approverTypes;

  constructor(private userService: LoggedInUserService,
    private sharedService: SharedService, 
    private requestAndLineTypeService: RequestAndLineTypeService, 
    private fb: FormBuilder, 
    @Inject(MAT_DIALOG_DATA) public data: { requestType: string, isEdit: boolean },
    public dialogRef: MatDialogRef<ManageRequestTypeComponent>) { }

  ngOnInit(): void {

    this.userService.currentUser$.subscribe(data => {
      this.user = data;
    });
    
    this.sharedService.getCodes('EmployeeType').subscribe({
      next: (data) => {
        this.employeeTypeCodes = data;
        if (this.data.isEdit) {
          this.loadRequest();
        }
        this.isLoading = false;
      }});

    this.form = this.fb.group({
      requestType: [''],
      description: ['', Validators.required],
      forEmployeeType: ['', Validators.required],
      forMode: ['', Validators.required],
      attachmentRequired: ['', Validators.required],
      howPaid: ['', Validators.required],
      skipAnalystReview: [Validators.required],
      doaType: [''],
      approver1Email: [''],
      approver1Name: [''],
      approver1Type: [''],
      approver2Email: [''],
      approver2Name: [''],
      approver2Type: [''],
      approver3Email: [''],
      approver3Name: [''],
      approver3Type: [''],
      approver4Email: [''],
      approver4Name: [''],
      approver5Email: [''],
      approver5Name: [''],
      enteredByEmail: [''],
      enteredByName: [''],
      dateEntered: [null],
      updatedByEmail: [''],
      updatedByName: [''],
      dateUpdated: [null]
    });
  }

  loadRequest(): void {
    this.requestAndLineTypeService.getRequestType(this.data.requestType).subscribe(data => {
      this.requestTypeData = data;

      this.requestAndLineTypeService.checkIfExistForGivenRequestType(this.data.requestType).subscribe(checkData => {
        this.isRequestTypeUsed = checkData;
        this.isLoading = false;
        this.form.patchValue(data);
      });

      
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onApproverSelected(approverNumber: string, event: any): void {
    this.form.get(approverNumber + "Email").patchValue(event.email);
    this.form.get(approverNumber + "Name").patchValue(event.fullName);
  }

  onApproverTypeChange(approverNumber: string, event: any): void {
    if (event === '') {
      
      this.form.get(approverNumber + "Email").patchValue('');
      this.form.get(approverNumber + "Name").patchValue('');
    }

    if (event === "*SUP") {
      this.form.get(approverNumber + "Email").patchValue('*SUP');
      this.form.get(approverNumber + "Name").patchValue('*Supervisor');
    }
  }

  onApproverClear(approverNumber: string): void {
    this.form.get(approverNumber + "Email").setValue('');
    this.form.get(approverNumber + "Name").setValue('');
  }

  submit(): void {
    if (this.form.valid && !this.isLoading) {

      if (this.data.isEdit) {
      this.requestTypeData = this.form.value;
      this.requestTypeData.updatedByEmail = this.user.userEmail;
      this.requestTypeData.updatedByName = this.user.userName;
      this.requestAndLineTypeService.updateRequestType(this.requestTypeData).subscribe({
        next: (data) => {
          if (data.isFailure) {
            this.errorResponse = data.error.description;
          } else {
            this.successResponse = "The request type has been updated.";
          }
        },
        error: () => {
          this.errorResponse = "Something went wrong. Please try again.";
        }
      });
      } else {
        this.requestTypeData = this.form.value;
        this.requestTypeData.enteredByEmail = this.user.userEmail;
        this.requestTypeData.enteredByName = this.user.userName;
        this.requestAndLineTypeService.addRequestType(this.requestTypeData).subscribe({
          next: (data) => {
            if (data.isFailure) {
              this.errorResponse = data.error.description;
            } else {
              this.successResponse = "The request type has been added.";
            }
          },
          error: () => {
            this.errorResponse = "Something went wrong. Please try again.";
          }
        });
      }
    }
  }

  clearMessages(): void {
    this.successResponse = undefined;
    this.errorResponse = undefined;
  }

  remove(): void {
    if (confirm("Please confirm that you want to delete this request type.")) {
      this.requestAndLineTypeService.deleteRequestType(this.requestTypeData.requestType).subscribe({
        next: (data) => {
          if (data.isFailure) {
            this.errorResponse = data.error.description;
          } else {
            this.successResponse = "The request type has been removed.";
          }
        },
        error: () => {
          this.errorResponse = "Something went wrong. Please try again.";
        }
      });
    }
  }
}
